/**
 * Client functions for Smart Lesson source fetching and image analysis.
 */

/**
 * Fetch content from a URL (article or YouTube video).
 * @param {string} url
 * @returns {Promise<{
 *   type: 'youtube'|'article',
 *   title: string, text: string, url: string,
 *   videoId?: string, channel?: string, thumbnailUrl?: string,
 *   durationSeconds?: number, hasTranscript?: boolean,
 *   sourceName?: string, domain?: string, wordCount?: number,
 *   imageUrl?: string, description?: string,
 * }>}
 */
export async function fetchLessonSource(url, signal) {
  const res = await fetch('/api/lessons/ai/fetch-source', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Source fetch failed (${res.status})`);
  return data;
}

/**
 * Analyze an image with GPT-4o Vision.
 * @param {File} file - image File object
 * @returns {Promise<{
 *   detectedType: 'text'|'scene'|'whiteboard'|'mixed',
 *   extractedText: string, sceneDescription: string,
 *   suggestedTitle: string, suggestedLevel: string, contentSummary: string,
 * }>}
 */
export async function analyzeLessonImage(file) {
  const base64 = await fileToBase64(file);
  const res = await fetch('/api/lessons/ai/analyze-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageData: base64, mimeType: file.type }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Image analysis failed (${res.status})`);
  return data;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result is "data:image/jpeg;base64,<base64>" — strip the prefix
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Format seconds → "3:42" or "1h 12m" */
export function formatDuration(seconds) {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
